import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notify } from '../../settings/toastSetting';
import { COLORS } from '../../settings/themeSettings';
import facturationService from '../../services/facturationService';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Chip,
    Stack,
    CircularProgress,
    InputAdornment,
    Grid
} from '@mui/material';
import {
    Receipt as FactureIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Add as AddIcon,
    Print as PrintIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const FacturationUnifiee = ({ hideAddButton = false }) => {
    const dispatch = useDispatch();
    const { me: user } = useSelector((state) => state.me || {});
    const navigate = useNavigate();

    const confirmDialog = useConfirmDialog();

    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [pagination, setPagination] = useState({
        page: 0,
        total: 0,
        totalPages: 0,
        limit: 10
    });

    const [filters, setFilters] = useState({
        search: '',
        statut: 'tous',
        dateDebut: null,
        dateFin: null,
        activite: 'tous'
    });

    const [instantSearch, setInstantSearch] = useState('');

    const fetchFactures = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: pagination.page + 1,
                limit: pagination.limit,
                search: filters.search,
                statut: filters.statut !== 'tous' ? filters.statut : undefined,
                activite: filters.activite !== 'tous' ? filters.activite : undefined,
                dateDebut: filters.dateDebut ? filters.dateDebut.toISOString().split('T')[0] : undefined,
                dateFin: filters.dateFin ? filters.dateFin.toISOString().split('T')[0] : undefined
            };

            const { page, limit, ...otherFilters } = params;
            const response = await facturationService.getFactures(page, limit, otherFilters);

            const isArray = Array.isArray(response);
            const data = isArray ? response : (response.data || []);

            setFactures(data);
            setPagination(prev => ({
                ...prev,
                total: isArray ? data.length : (response.total || 0),
                totalPages: isArray ? 1 : (response.last_page || 0),
                page: isArray ? 0 : ((response.current_page || 1) - 1)
            }));

        } catch (err) {
            console.error('Erreur lors du chargement des factures:', err);
            setError('Erreur lors du chargement des factures. Veuillez réessayer.');
            notify('Erreur lors du chargement des factures', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    useEffect(() => {
        fetchFactures();
    }, [fetchFactures]);

    const handleChangePage = (event, newPage) => {
        setPagination(prev => ({
            ...prev,
            page: newPage
        }));
    };

    const handleChangeRowsPerPage = (event) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(event.target.value, 10),
            page: 0
        }));
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPagination(prev => ({
            ...prev,
            page: 0
        }));
    };

    const handleInstantSearch = (value) => {
        setInstantSearch(value);
        clearTimeout(window.searchTimeout);
        window.searchTimeout = setTimeout(() => {
            handleFilterChange('search', value);
        }, 300);
    };

    const filteredFactures = factures.filter(facture => {
        // Filtre texte instantané
        if (instantSearch) {
            const searchLower = instantSearch.toLowerCase();
            const numeroFacture = (facture.numeroFacture || '').toLowerCase();
            const numeroDossier = (facture.numeroDossier || facture.dossier?.reference || facture.reference || '').toLowerCase();
            const clientNom = facture.client ?
                (facture.client.libelle || facture.client.nom || facture.client.structure || '').toLowerCase() :
                '';
            if (!numeroFacture.includes(searchLower) &&
                !numeroDossier.includes(searchLower) &&
                !clientNom.includes(searchLower)) {
                return false;
            }
        }

        // Filtre activité : utilise typeActivite retourné directement par l'API
        if (filters.activite !== 'tous') {
            const type = (facture.typeActivite || '').toLowerCase();
            const filterValue = filters.activite.toLowerCase();
            if (filterValue === 'tourisme') {
                 // Si on filtre par tourisme générique, on exclut billetterie
                 if (type === 'billetterie') return false;
            } else {
                 // Sinon correspondance exacte (billetterie, circuit, sejours, transfert)
                 if (type !== filterValue) return false;
            }
        }

        return true;
    });

    const formatActivite = (type) => {
        if (!type) return 'Tourisme';
        const t = type.toLowerCase();
        if (t === 'billetterie') return 'Billetterie';
        if (t === 'circuit') return 'Circuit';
        if (t === 'sejours' || t === 'séjours') return 'Séjours';
        if (t === 'transfert') return 'Transfert';
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const emptyRows =
        pagination.page > 0 ? Math.max(0, (1 + pagination.page) * pagination.limit - filteredFactures.length) : 0;

    const handleDeleteFacture = async (id) => {
        const confirmed = await confirmDialog.confirm({
            title: 'Supprimer la facture',
            message: 'Êtes-vous sûr de vouloir supprimer cette facture ? Cette action est irréversible.',
            confirmText: 'Supprimer',
            cancelText: 'Annuler',
            severity: 'error'
        });

        if (confirmed) {
            confirmDialog.setLoading(true);
            try {
                await facturationService.deleteFacture(id);
                notify('Facture supprimée avec succès', 'success');
                fetchFactures();
            } catch (err) {
                console.error('Erreur lors de la suppression de la facture:', err);
                notify('Erreur lors de la suppression de la facture', 'error');
            } finally {
                confirmDialog.setLoading(false);
            }
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR').format(amount || 0);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f9fafb', minHeight: '100vh' }}>
            <Breadcrumb />

            {/* Clean, Flat Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FactureIcon color="action" />
                        Facturation Unifiée
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                        Gestion de l'ensemble de vos factures clients.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchFactures}
                        disabled={loading}
                        sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}
                    >
                        Actualiser
                    </Button>
                    {!hideAddButton && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/facturation/nouvelle')}
                            disabled={loading}
                            sx={{ textTransform: 'none', backgroundColor: COLORS.primary }}
                        >
                            Nouvelle Facture
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Flat KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    { title: 'Total Facturé', value: factures.reduce((s, f) => s + (f.total || 0), 0), color: COLORS.primary },
                    { title: 'Total Recouvré', value: factures.reduce((s, f) => s + (f.totalPaye || 0), 0), color: '#059669' },
                    { title: 'Reste à Recouvrer', value: factures.reduce((s, f) => s + (f.solde || 0), 0), color: '#dc2626' },
                ].map((stat, i) => (
                    <Grid item xs={12} md={4} key={i}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', borderColor: '#e5e7eb' }}>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                                {stat.title}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: stat.color }}>
                                {formatCurrency(stat.value)} <Typography component="span" variant="body2" sx={{ color: '#9ca3af' }}>CFA</Typography>
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Minimalist Filters Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: '8px', borderColor: '#e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FilterIcon sx={{ color: '#9ca3af', mr: 1, fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                        Filtres de recherche
                    </Typography>
                </Box>
                
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="N°, Dossier ou Client..."
                            value={instantSearch}
                            onChange={(e) => handleInstantSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Statut Global</InputLabel>
                            <Select
                                value={filters.statut}
                                onChange={(e) => handleFilterChange('statut', e.target.value)}
                                label="Statut Global"
                            >
                                <MenuItem value="tous">Tous les statuts</MenuItem>
                                <MenuItem value="payée">Payée</MenuItem>
                                <MenuItem value="en attente">En attente / Partiel</MenuItem>
                                <MenuItem value="annulée">Annulée</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Activité</InputLabel>
                            <Select
                                value={filters.activite}
                                onChange={(e) => handleFilterChange('activite', e.target.value)}
                                label="Activité"
                            >
                                <MenuItem value="tous">Toutes les activités</MenuItem>
                                <MenuItem value="Billetterie">Billetterie</MenuItem>
                                <MenuItem value="Circuit">Circuit</MenuItem>
                                <MenuItem value="Sejours">Séjours</MenuItem>
                                <MenuItem value="Transfert">Transfert</MenuItem>
                                <MenuItem value="Tourisme">Tourisme (Autres)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Date de début"
                                value={filters.dateDebut}
                                onChange={(newValue) => handleFilterChange('dateDebut', newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Date de fin"
                                value={filters.dateFin}
                                onChange={(newValue) => handleFilterChange('dateFin', newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    }
                                }}
                            />
                        </Grid>
                    </LocalizationProvider>
                    <Grid item xs={12} md={1}>
                        <Button
                            fullWidth
                            color="inherit"
                            onClick={() => {
                                setInstantSearch('');
                                setFilters({ search: '', statut: 'tous', dateDebut: null, dateFin: null, activite: 'tous' });
                            }}
                            sx={{ textTransform: 'none', color: '#6b7280' }}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Clean Table */}
            <Paper variant="outlined" sx={{ width: '100%', borderRadius: '8px', borderColor: '#e5e7eb', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {[
                                    { label: 'Date', align: 'left' },
                                    { label: 'N° Facture', align: 'left' },
                                    { label: 'Dossier / Réf', align: 'left' },
                                    { label: 'Client', align: 'left' },
                                    { label: 'Activité', align: 'center' },
                                    { label: 'Montant Total', align: 'right' },
                                    { label: 'Déjà Payé', align: 'right' },
                                    { label: 'Solde', align: 'right' },
                                    { label: 'État', align: 'center' },
                                    { label: '', align: 'center' }
                                ].map((col, index) => (
                                    <TableCell 
                                        key={index}
                                        align={col.align}
                                        sx={{ 
                                            backgroundColor: '#f9fafb',
                                            color: '#6b7280',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            borderBottom: '1px solid #e5e7eb',
                                            py: 1.5
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={30} sx={{ color: '#9ca3af' }} />
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <Typography color="error" variant="body2">{error}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredFactures.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Aucune facture trouvée.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredFactures.map((facture) => {
                                    const solde = facture.solde || 0;
                                    const total = facture.total || 0;
                                    const paye = facture.totalPaye || 0;
                                    const isPaid = solde <= 0 && total > 0;
                                    const isPartial = solde > 0 && paye > 0;

                                    return (
                                        <TableRow
                                            hover
                                            key={facture.id}
                                            onDoubleClick={() => {
                                                if (facture.dossier) {
                                                    const dossierId = typeof facture.dossier === 'object' ? facture.dossier.id : facture.dossier;
                                                    const type = (facture.dossier && typeof facture.dossier === 'object' ? facture.dossier.type : null) || 'circuit';
                                                    navigate(`/dossiers/${type}/${dossierId}/factures/${facture.id}/edit`);
                                                } else {
                                                    const clientId = typeof facture.client === 'object' ? facture.client.id : facture.client;
                                                    navigate(`/billeterie/facture/${clientId}/update/${facture.id}`);
                                                }
                                            }}
                                            sx={{ '&:last-child td': { borderBottom: 0 }, cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ color: '#4b5563', fontSize: '0.85rem' }}>
                                                {facture.date 
                                                    ? new Date(facture.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) 
                                                    : (facture.createdAt 
                                                        ? new Date(facture.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) 
                                                        : 'Date non renseignée')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 500, color: '#111827', fontSize: '0.85rem' }}>
                                                {facture.numeroFacture || `F-${facture.id}`}
                                            </TableCell>
                                            <TableCell sx={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                                {facture.reference || facture.numeroDossier || facture.dossier?.reference || 'Référence non attribuée'}
                                            </TableCell>
                                            <TableCell sx={{ color: '#111827', fontSize: '0.85rem' }}>
                                                {typeof facture.client === 'object' && facture.client !== null
                                                    ? (facture.client.libelle || facture.client.nom || facture.client.structure || 'Client inconnu')
                                                    : (facture.client || '-')}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '0.8rem' }}>
                                                    {formatActivite(facture.typeActivite)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#111827', fontSize: '0.85rem' }}>
                                                {formatCurrency(total)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#059669', fontSize: '0.85rem' }}>
                                                {formatCurrency(paye)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 500, color: solde > 0 ? '#dc2626' : '#059669', fontSize: '0.85rem' }}>
                                                {formatCurrency(solde)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={isPaid ? 'Soldée' : (isPartial ? 'Partiel' : 'Impayée')}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ 
                                                        borderColor: isPaid ? '#10b981' : (isPartial ? '#f59e0b' : '#ef4444'),
                                                        color: isPaid ? '#10b981' : (isPartial ? '#f59e0b' : '#ef4444'),
                                                        fontSize: '0.7rem',
                                                        height: '20px'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0} justifyContent="center">
                                                    <Tooltip title="Détails">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#6b7280' }}
                                                            onClick={() => {
                                                                const clientId = typeof facture.client === 'object' ? facture.client.id : facture.client;
                                                                const typeActivite = (facture.typeActivite || '').toLowerCase();
                                                                
                                                                if (typeActivite === 'billetterie' || !facture.dossier) {
                                                                    navigate(`/billeterie/factures/${clientId}/details/${facture.id}`);
                                                                } else {
                                                                    const dossierId = typeof facture.dossier === 'object' ? facture.dossier.id : facture.dossier;
                                                                    const type = (facture.dossier && typeof facture.dossier === 'object' ? facture.dossier.type : null) || 'circuit';
                                                                    navigate(`/dossiers/${type}/${dossierId}/factures/${facture.id}`);
                                                                }
                                                            }}
                                                        >
                                                            <SearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Imprimer">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#6b7280' }}
                                                            onClick={() => {
                                                                const clientId = typeof facture.client === 'object' ? facture.client.id : facture.client;
                                                                const typeActivite = (facture.typeActivite || '').toLowerCase();
                                                                
                                                                if (typeActivite === 'billetterie' || !facture.dossier) {
                                                                    navigate(`/billeterie/facture/${clientId}/${facture.id}/pdf`);
                                                                } else {
                                                                    const dossierId = typeof facture.dossier === 'object' ? facture.dossier.id : facture.dossier;
                                                                    const type = (facture.dossier && typeof facture.dossier === 'object' ? facture.dossier.type : null) || 'circuit';
                                                                    navigate(`/dossiers/${type}/${dossierId}/factures/${facture.id}`);
                                                                }
                                                            }}
                                                        >
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Supprimer">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#ef4444' }}
                                                            onClick={() => handleDeleteFacture(facture.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                            {!loading && emptyRows > 0 && (
                                <TableRow style={{ height: 43 * emptyRows }}>
                                    <TableCell colSpan={10} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <Box sx={{ borderTop: '1px solid #e5e7eb', px: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={pagination.total}
                        rowsPerPage={pagination.limit}
                        page={pagination.page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Lignes :"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `+${to}`}`}
                        disabled={loading}
                        sx={{ color: '#4b5563' }}
                    />
                </Box>
            </Paper>

            <ConfirmDialog
                open={confirmDialog.dialogState.open}
                title={confirmDialog.dialogState.title}
                message={confirmDialog.dialogState.message}
                confirmText={confirmDialog.dialogState.confirmText}
                cancelText={confirmDialog.dialogState.cancelText || 'Annuler'}
                severity={confirmDialog.dialogState.severity}
                loading={confirmDialog.dialogState.loading}
                onConfirm={confirmDialog.handleConfirm}
                onCancel={confirmDialog.handleCancel}
            />
        </Box>
    );
};

export default FacturationUnifiee;